// material-ui
import {
  Button,
  ButtonGroup,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  InputLabel,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// third-party
import _ from 'lodash';
import * as Yup from 'yup';
import { useFormik, Form, FormikProvider, FormikValues } from 'formik';

// project-imports
import { dispatch } from 'store';
import { openSnackbar } from 'store/reducers/snackbar';
// assets
import serverCall from 'api/serverCall';
import api from 'api/api';
import LoadingButton from 'components/@extended/LoadingButton';
import { TransactionType } from 'enum/transaction-type';
import { useState } from 'react';

// constant
const getInitialValues = (order: FormikValues | null) => {
  const newOrder = {
    price: '',
    quantity: '',
    transactionType: ''
  };

  if (order) {
    newOrder.price = order.price;
    newOrder.quantity = order.quantity;
    newOrder.transactionType = order.transactionType;

    return _.merge({}, newOrder, order);
  }

  return newOrder;
};

// ==============================|| ORDER / EDIT ||============================== //

export interface Props {
  order: any;
  fetchAllOrders: () => void;
  onCancel: () => void;
}

const validationSchema = Yup.object().shape({
  price: Yup.number().typeError('Price must be a number').min(1, 'Price cannot be zero or negative').required('Price is required'),
  quantity: Yup.number()
    .typeError('Quantity must be a number')
    .min(1, 'Quantity cannot be zero or negative')
    .integer('Quantity must be a whole number')
    .required('Quantity is required'),
  transactionType: Yup.string().required('Transaction Type is required')
});

const ModifyOrder = ({ order, onCancel, fetchAllOrders }: Props) => {
  //@ts-ignore
  const [transactionType, setTransactionType] = useState<TransactionType>(order.transactionType);
  const formik = useFormik({
    initialValues: getInitialValues(order!),
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      try {
        const data: any = { ...values };
        const requestBody = data.orderList.map((item: any) => ({
          userId: item.userId,
          quantity: data.quantity,
          stockId: item?.stockId,
          transactionType: transactionType,
          requestId: item.requestId,
          price: data.price,
          validity: 'DAY',
          product: 'CNC',
          triggerPrice: data.price,
          orderType: data.orderType
        }));
        serverCall.post(api.modifyOrder(), requestBody, (response: any, error: any) => {
          if (error) {
            const errorMessage = error?.response?.data?.error;
            dispatch(
              openSnackbar({
                open: true,
                close: true,
                message: errorMessage || 'Failed to modify the order',
                variant: 'alert',
                alert: {
                  color: 'error'
                },
                anchorOrigin: {
                  vertical: 'top',
                  horizontal: 'right'
                }
              })
            );
          } else {
            if (response?.orderList.length !== 0) {
              fetchAllOrders();
              onCancel();
              dispatch(
                openSnackbar({
                  open: true,
                  close: true,
                  message: 'Order modified successfully.',
                  variant: 'alert',
                  alert: {
                    color: 'success'
                  },
                  anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right'
                  }
                })
              );
            } else {
              dispatch(
                openSnackbar({
                  open: true,
                  close: true,
                  message: 'Failed to modify the order',
                  variant: 'alert',
                  alert: {
                    color: 'error'
                  },
                  anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right'
                  }
                })
              );
            }
          }
        });
        setSubmitting(false);
      } catch (error) {
        console.error(error);
      }
    }
  });

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps } = formik;

  return (
    <>
      <FormikProvider value={formik}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <DialogTitle>Modify Order</DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 2.5 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={12}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={1.25}>
                        <InputLabel htmlFor="order-price">Price</InputLabel>
                        <TextField
                          fullWidth
                          type="number"
                          inputProps={{ min: 1 }}
                          id="order-price"
                          placeholder="Enter Price"
                          {...getFieldProps('price')}
                          error={Boolean(touched.price && errors.price)}
                          helperText={touched.price && errors.price}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={1.25}>
                        <InputLabel htmlFor="order-quantity">Quantity</InputLabel>
                        <TextField
                          fullWidth
                          type="number"
                          inputProps={{ min: 1 }}
                          id="order-quantity"
                          placeholder="Enter Quantity"
                          {...getFieldProps('quantity')}
                          error={Boolean(touched.quantity && errors.quantity)}
                          helperText={touched.quantity && errors.quantity}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6} lg={6}>
                      <InputLabel htmlFor="order-transactionType">Transaction Type</InputLabel>
                      <ButtonGroup fullWidth sx={{ mt: 1.5, height: 43 }} variant="outlined" aria-label="Loading button group">
                        {transactionType === TransactionType.BUY ? (
                          <Button color="primary" variant={'outlined'} sx={{ pointerEvents: 'none', opacity: 0.5 }}>
                            <Typography sx={{ fontWeight: 'bold' }}>BUY</Typography>
                          </Button>
                        ) : (
                          <Button color="error" variant={'outlined'} sx={{ pointerEvents: 'none', opacity: 0.5 }}>
                            <Typography sx={{ fontWeight: 'bold' }}>SELL</Typography>
                          </Button>
                        )}
                      </ButtonGroup>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={1.25}>
                        <InputLabel htmlFor="order-stock">Stock</InputLabel>
                        <TextField
                          disabled={true}
                          fullWidth
                          inputProps={{ readOnly: true }}
                          id="order-stock"
                          value={order.stockName || order.stockId}
                          sx={{
                            '&:hover': {
                              borderColor: 'transparent'
                            },
                            '&:focus': {
                              outline: 'none'
                            }
                          }}
                        />
                      </Stack>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 2.5 }}>
              <Grid container justifyContent="end" alignItems="center">
                <Grid item>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Button color="error" onClick={onCancel}>
                      Close
                    </Button>
                    <LoadingButton
                      type="submit"
                      loading={isSubmitting}
                      fullWidth
                      disabled={isSubmitting}
                      variant="contained"
                      // loadingPosition="start"
                      style={{ cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                    >
                      <span style={{ marginLeft: isSubmitting ? '20px' : '' }}>{isSubmitting ? 'Saving... ' : 'Modify'}</span>
                    </LoadingButton>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </Form>
        </LocalizationProvider>
      </FormikProvider>
    </>
  );
};

export default ModifyOrder;
